import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/hooks/useAuth";
import { CartProvider } from "@/contexts/CartContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import SuspendedGuard from "@/components/auth/SuspendedGuard";
import ScrollToTop from "@/components/ScrollToTop";

import Index from "./pages/Index";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import History from "./pages/History";
import Generate from "./pages/Generate";
import Pricing from "./pages/Pricing";
import AIProgram from "./pages/AIProgram";
import Admin from "./pages/Admin";
import Settings from "./pages/Settings";
import AIChat from "./pages/AIChat";
import Example from "./pages/Example";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import FAQ from "./pages/FAQ";
import AIProgramDetail from "./pages/AIProgramDetail";
import Professional from "./pages/Professional";
import CreditHistory from "./pages/CreditHistory";
import AllServices from "./pages/AllServices";
import Watermark from "./pages/Watermark";
import Sora from "./pages/Sora";
import NotFound from "./pages/NotFound";
import VideoDance from "./pages/VideoDance";
import Manual from "./pages/Manual";
import Contact from "./pages/Contact";
import Transform from "./pages/Transform";
import Article from "./pages/Article";
import Promo from "./pages/Promo";
import VideoBulk from "./pages/VideoBulk";
import PhotoBulk from "./pages/PhotoBulk";
import VideoMerge from "./pages/VideoMerge";
import MyPrograms from "./pages/MyPrograms";
import PromptAI from "./pages/PromptAI";
import CoverGenerator from "./pages/CoverGenerator";
import TextToSpeech from "./pages/TextToSpeech";
import TextToDialogue from "./pages/TextToDialogue";
import Grok from "./pages/Grok";
import GrokBulk from "./pages/GrokBulk";
import MagicButton from "./pages/MagicButton";
import Promo1290 from "./pages/Promo1290";
import RemoveBackground from "./pages/RemoveBackground";
import AIForBusiness from "./pages/AIForBusiness";
import { BulkGenerationProvider } from "@/contexts/BulkGenerationContext";


const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <ThemeProvider defaultTheme="light" storageKey="ai-gen-theme" attribute="class">
      <LanguageProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <ScrollToTop />
                <CartProvider>

                  <SuspendedGuard>
                    <Routes>
                      <Route path="/" element={<Index />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/my-programs" element={<MyPrograms />} /> {/* Added Route */}
                      <Route path="/history" element={<History />} />
                      <Route path="/history/:type" element={<History />} />
                      <Route path="/ai-chat" element={<AIChat />} />
                      <Route path="/ai-chat/:tab" element={<AIChat />} />

                      <Route path="/prompt-ai" element={<PromptAI />} />

                      <Route path="/generate/sora" element={<Sora />} />
                      <Route path="/generate/sora/:type" element={<Sora />} />
                      <Route path="/generate/grok" element={<Grok />} />
                      <Route path="/generate/grok/:type" element={<Grok />} />
                      <Route path="/generate/grokbulk" element={<GrokBulk />} />
                      <Route path="/generate/videodance" element={<VideoDance />} />
                      <Route path="/generate/transform" element={<Transform />} />
                      <Route path="/generate/videobulk" element={<VideoBulk />} />
                      <Route path="/generate/photobulk" element={<PhotoBulk />} />
                      <Route path="/generate/videomerge" element={<VideoMerge />} />
                      <Route path="/generate/magic" element={<MagicButton />} />
                      <Route path="/generate/cover" element={<CoverGenerator />} />
                      <Route path="/generate/text-to-speech" element={<TextToSpeech />} />
                      <Route path="/generate/text-to-dialogue" element={<TextToDialogue />} />
                      <Route path="/generate" element={<Generate />} />
                      <Route path="/generate/:type" element={<Generate />} />
                      <Route path="/professional" element={<Professional />} />
                      <Route path="/pricing" element={<Pricing />} />
                      <Route path="/credit-history" element={<CreditHistory />} />
                      <Route path="/ai-program" element={<AIProgram />} />
                      <Route path="/ai-program/:id" element={<AIProgramDetail />} />
                      <Route path="/admin" element={<Admin />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/example" element={<Example />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/manual" element={<Manual />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/all" element={<AllServices />} />
                      <Route path="/watermark" element={<Watermark />} />
                      <Route path="/remove-background" element={<RemoveBackground />} />
                      <Route path="/article/:id" element={<Article />} />
                      <Route path="/promo" element={<Promo />} />
                      <Route path="/promo/1290" element={<Promo1290 />} />
                      <Route path="/courses/ai-for-business" element={<AIForBusiness />} />
                      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </SuspendedGuard>
                </CartProvider>
              </BrowserRouter>
            </TooltipProvider>
          </AuthProvider>
        </QueryClientProvider>
      </LanguageProvider>
    </ThemeProvider>
  </ErrorBoundary>
);

export default App;
